import React, { useContext, useEffect } from 'react';
import s from './DeviceAuthorize.scss';
import { AppContextProvider } from '../AppLoader';
import { Box, Loader } from '@wix/design-system';
import QRCode from 'react-qr-code';
import { dataHooks } from '../../dataHooks';
import { DialogFormContainer } from '../DialogContainer';
import { BodyContainer } from '../AuthPage/AuthPage';
import { keys } from '../../translationsKeys';
import { EXPERIMENTS } from '../../utils/constants';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ROUTES } from '../../routes';
import {
  ThemedHeading,
  ThemedText,
  ThemedButton,
  ThemedTextButton,
} from '../ThemedComponents';

interface DeviceAuthorizeProps {}

export const DeviceAuthorize: React.FC<DeviceAuthorizeProps> = observer(() => {
  const {
    rootStore: {
      deviceAuthorizeStore,
      experiments,
      displayStore: { isMobile, isSymphony },
    },
  } = useContext(AppContextProvider);
  const { t } = useTranslation();
  const location = useLocation();
  // Manual user_code entry isn't supported yet — gate the code display behind a FT.
  const showUserCode = experiments.enabled(
    EXPERIMENTS.DEVICE_AUTH_SHOW_USER_CODE,
  );

  useEffect(() => {
    deviceAuthorizeStore.start();
    return () => deviceAuthorizeStore.dispose();
  }, [deviceAuthorizeStore]);

  const { status } = deviceAuthorizeStore;
  const isError = status === 'error';
  const qrReady = status === 'pending' && !!deviceAuthorizeStore.qrValue;

  // Symphony renders a brand-specific variant of this page per the Symphony QA
  // Figma: a back button next to the logo, an Archivo display title, an
  // instructional subtitle, the QR, and a numbered "how to log in" step list —
  // replacing the generic CLI flow's "waiting…" indicator and "sign in another
  // way" escape hatch. Every other caller (CLI device login) keeps the original
  // layout unchanged.
  // New copy per the Symphony QA Figma. Uses fresh `.steps.*` keys (not the
  // pre-existing `.title`/`.subtitle` keys, whose Babel values would otherwise
  // win over these defaults) so the redesigned copy renders until Babel + the
  // step keys below are localized.
  const title = isSymphony
    ? t('device.authorize.symphony.steps.title', {
        defaultValue: 'Log in with the Symphony app',
      })
    : t(keys['device.authorize.title']);
  const subtitle = isSymphony
    ? t('device.authorize.symphony.steps.subtitle', {
        defaultValue: 'Follow these steps to log in on this computer.',
      })
    : t(keys['device.authorize.subtitle']);

  return (
    <Box direction="vertical" width="100%" align="center">
      <DialogFormContainer
        dataHook={dataHooks.deviceAuthorize.container}
        formClasses={[s.deviceAuthorize]}
        // Symphony QA Figma adds a back button next to the logo; the generic CLI
        // device-login flow keeps its header button-less.
        mainHeaderProps={{ showBackButton: isSymphony }}
      >
        <BodyContainer className={s.cardContentPadding}>
          <Box
            marginBottom={isSymphony ? 2 : 3}
            verticalAlign="middle"
            textAlign="center"
          >
            <ThemedHeading
              appearance={isMobile ? 'H2' : 'H1'}
              dataHook={dataHooks.deviceAuthorize.title}
            >
              {title}
            </ThemedHeading>
          </Box>

          {isError ? (
            <Box
              marginBottom={3}
              direction="vertical"
              align="center"
              textAlign="center"
              gap="SP4"
              dataHook={dataHooks.deviceAuthorize.error}
            >
              <ThemedText size="medium">
                {deviceAuthorizeStore.errorMessage}
              </ThemedText>
              <ThemedButton
                size="large"
                skin="standard"
                onClick={() => deviceAuthorizeStore.start()}
                dataHook={dataHooks.deviceAuthorize.retry}
              >
                {t(keys['device.authorize.retry'])}
              </ThemedButton>
            </Box>
          ) : (
            // Loading and pending share the exact same layout — only the QR slot
            // swaps (spinner ↔ QR), so the card height never jumps on transition.
            <>
              <Box marginBottom={3} verticalAlign="middle" textAlign="center">
                <ThemedText
                  size={isSymphony ? 'medium' : 'small'}
                  className={isSymphony ? s.symphonySubtitle : undefined}
                  dataHook={dataHooks.deviceAuthorize.subtitle}
                >
                  {subtitle}
                </ThemedText>
              </Box>
              <Box marginBottom={3} className={s.qrSlot}>
                {qrReady ? (
                  <Box
                    align="center"
                    className={s.qrWrapper}
                    dataHook={dataHooks.deviceAuthorize.qrCode}
                  >
                    <QRCode value={deviceAuthorizeStore.qrValue} size={180} />
                  </Box>
                ) : (
                  <Loader size="medium" />
                )}
              </Box>
              {showUserCode && qrReady && (
                <Box
                  marginBottom={3}
                  direction="vertical"
                  align="center"
                  textAlign="center"
                >
                  <ThemedText size="small">
                    {t(keys['device.authorize.user.code.label'])}
                  </ThemedText>
                  <ThemedHeading
                    appearance="H3"
                    dataHook={dataHooks.deviceAuthorize.userCode}
                  >
                    {deviceAuthorizeStore.userCode}
                  </ThemedHeading>
                </Box>
              )}
              {isSymphony ? (
                // Symphony QA Figma: a numbered "how to log in" step list under
                // the QR (no "waiting…" indicator). Bold sub-phrases mark the
                // in-app actions the user has to find.
                <Box
                  direction="vertical"
                  width="100%"
                  align="center"
                  dataHook={dataHooks.deviceAuthorize.steps}
                >
                  <ol className={s.symphonySteps}>
                    <li>
                      <ThemedText size="medium">
                        <Trans
                          i18nKey="device.authorize.symphony.step1"
                          defaults="Open the Symphony app on your phone."
                        />
                      </ThemedText>
                    </li>
                    <li>
                      <ThemedText size="medium">
                        <Trans
                          i18nKey="device.authorize.symphony.step2"
                          defaults="Go to <b>Account Settings</b> and tap <b>Connect Symphony on Desktop</b>."
                          components={{ b: <b /> }}
                        />
                      </ThemedText>
                    </li>
                    <li>
                      <ThemedText size="medium">
                        <Trans
                          i18nKey="device.authorize.symphony.step3"
                          defaults="Scan this QR code, then tap <b>Log In</b> on your phone."
                          components={{ b: <b /> }}
                        />
                      </ThemedText>
                    </li>
                  </ol>
                </Box>
              ) : (
                <Box
                  marginBottom={3}
                  align="center"
                  verticalAlign="middle"
                  gap="SP2"
                  dataHook={dataHooks.deviceAuthorize.waiting}
                >
                  <Loader size="tiny" />
                  <ThemedText size="small">
                    {t(keys['device.authorize.waiting'])}
                  </ThemedText>
                </Box>
              )}
            </>
          )}

          {/* Low-prominence escape hatch to the regular sign-in flow. Symphony
              replaces this with the footer guidance above, per Figma. */}
          {!isSymphony && (
            <Box marginTop={3} verticalAlign="middle">
              <ThemedTextButton
                size="small"
                skin="dark"
                underline="always"
                as={Link}
                to={ROUTES.EMAIL_STEP + location.search}
                dataHook={dataHooks.deviceAuthorize.regularSignin}
              >
                {t(keys['device.authorize.regular.signin.cta'])}
              </ThemedTextButton>
            </Box>
          )}
        </BodyContainer>
      </DialogFormContainer>
    </Box>
  );
});
